import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Tooltip, Snackbar, Alert, IconButton,
} from '@mui/material';
import { BugReport as BugIcon } from '@mui/icons-material';
import api from '../services/api';

// Footer "Report a bug" button. Lives in the layout footer (right side)
// rather than as a FAB, so it stays out of the way of page content.
// Submits to POST /api/bug-reports and shows a thank-you snackbar.
// No AI reply, no follow-up — operator triages in /operator/bug-reports.
function BugReportFab() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [thankOpen, setThankOpen] = useState(false);

  const reset = () => { setMessage(''); setErr(null); };
  const close = () => { if (!busy) { setOpen(false); reset(); } };

  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 3) { setErr('Please describe the issue.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await api.post('/api/bug-reports', {
        message: trimmed,
        page_url: window.location.pathname + window.location.search,
      });
      setOpen(false);
      reset();
      setThankOpen(true);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="Report a bug or issue" placement="top">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="report a bug"
          sx={{
            color: '#d4a017',
            '&:hover': {
              backgroundColor: 'rgba(212, 160, 23, 0.08)',
              color: '#e6b830',
            },
          }}
        >
          <BugIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Report a bug or issue</DialogTitle>
        <DialogContent>
          {/* Plain HTML textarea — three rounds of MUI TextField + DialogContent
              tweaks couldn't make clicks land on it (hover-highlight worked,
              click did nothing). Bypassing MUI's input wrapper entirely with
              an explicit pointer-events:auto + zIndex stacking-context anchor
              eliminates any "something else is on top" possibility. */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What went wrong, or what would make this work better?"
            autoFocus
            rows={6}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              fontFamily: 'inherit',
              color: 'inherit',
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(212,160,23,0.25)',
              borderRadius: '6px',
              resize: 'vertical',
              outline: 'none',
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 1,
              boxSizing: 'border-box',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#d4a017'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(212,160,23,0.25)'; }}
          />
          {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={submit} variant="contained" disabled={busy || message.trim().length < 3}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={thankOpen}
        autoHideDuration={4000}
        onClose={() => setThankOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setThankOpen(false)}>
          Thanks for the report — we'll take a look.
        </Alert>
      </Snackbar>
    </>
  );
}

export default BugReportFab;
